import { MealPlanResponse, MealPlanSubstitutions, DayMeals } from '../types/mealPlan';
import { NutritionCalculation } from './nutritionCalculationService';
import { getAccessToken } from './authApi';
import { getTrainingMealSchedule } from '../utils/mealSchedule';

interface PatientData {
  name: string;
  age: number;
  weight: number;
  height: number;
  gender: 'male' | 'female';
  activityLevel: 'sedentary' | 'light' | 'moderate' | 'active' | 'very_active';
  goal: 'weight_loss' | 'maintenance' | 'muscle_gain';
  medicalConditions?: string;
  allergies?: string;
  preferences?: string;
  trainingTime?: string;
  medicalHistory?: string[];
  medications?: string[];
  supplements?: string[];
  dietaryRestrictions?: string[];
  dislikes?: string[];
}

const API_BASE = (process.env.REACT_APP_API_BASE_URL || '/api').replace(/\/$/, '');

export class DeepSeekService {
  private async apiRequest<T>(action: string, method: 'GET' | 'POST' = 'GET', body?: Record<string, unknown>): Promise<T> {
    const token = getAccessToken();
    if (!token) throw new Error('Sessão ausente. Entre novamente para usar a IA.');
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 110000);
    let response: Response;
    try {
      response = await fetch(`${API_BASE}/ai.php?action=${encodeURIComponent(action)}`, {
        method,
        headers: { Accept: 'application/json', ...(body ? { 'Content-Type': 'application/json' } : {}), Authorization: `Bearer ${token}` },
        body: body ? JSON.stringify(body) : undefined,
        cache: 'no-store',
        signal: controller.signal,
      });
    } catch (error: any) {
      if (error?.name === 'AbortError') throw new Error('A DeepSeek demorou demais para responder. Tente novamente.');
      throw error;
    } finally {
      window.clearTimeout(timeout);
    }
    const result = await response.json().catch(() => null);
    if (!response.ok || !result?.success) throw new Error(result?.error || 'Não foi possível comunicar com a DeepSeek.');
    return result.data as T;
  }

  async getConfigurationStatus(): Promise<{ provider: string; configured: boolean }> {
    return this.apiRequest('status');
  }

  async testConnection(): Promise<{ ok: boolean; message?: string }> {
    try {
      await this.apiRequest('test', 'POST', {});
      return { ok: true };
    } catch (error: any) {
      return { ok: false, message: error?.message || 'Falha ao validar a configuração DeepSeek.' };
    }
  }

  private async requestDeepSeek(prompt: string, systemPrompt?: string): Promise<string> {
    const result = await this.apiRequest<{ content: string; finishReason?: string }>('generate', 'POST', { prompt, systemPrompt, jsonMode: true });
    if (!result?.content) throw new Error('A DeepSeek retornou uma resposta vazia.');
    return result.content;
  }

  private getActivityLevelText(level: string): string {
    const map: Record<string, string> = {
      sedentary: 'Sedentário',
      light: 'Leve',
      moderate: 'Moderado',
      active: 'Ativo',
      very_active: 'Muito Ativo',
    };
    return map[level] || level;
  }

  private getGoalText(goal: string): string {
    const map: Record<string, string> = {
      weight_loss: 'Emagrecimento',
      maintenance: 'Manutenção',
      muscle_gain: 'Ganho de Massa Muscular',
    };
    return map[goal] || goal;
  }

  /**
   * Retorna orientações nutricionais específicas baseadas nas condições médicas do paciente
   */
  private getMedicalConditionsGuidance(conditions: string): string {
    const conditionsLower = conditions.toLowerCase();
    let guidance = `Condição informada: ${conditions}\n`;

    // Diabetes
    if (conditionsLower.includes('diabet')) {
      guidance += `\nOrientações para Diabetes:
- Evitar: açúcares refinados, doces, refrigerantes, sucos industrializados, carboidratos simples
- Priorizar: carboidratos complexos (integral, quinoa, aveia), fontes de fibras, proteínas magras
- Dica Nutricional: Distribuir carboidratos ao longo do dia, preferir alimentos de baixo índice glicêmico
`;
    }

    // Hipertensão
    if (conditionsLower.includes('hipertens') || conditionsLower.includes('pressão alta')) {
      guidance += `\nOrientações para Hipertensão:
- Evitar: sal em excesso, alimentos processados, embutidos, enlatados, temperos prontos
- Priorizar: frutas, vegetais, grãos integrais, alimentos ricos em potássio (banana, abacate)
- Dica Nutricional: Reduzir sódio a menos de 2g/dia, usar ervas e especiarias naturais
`;
    }

    // Colesterol Alto
    if (conditionsLower.includes('colesterol')) {
      guidance += `\nOrientações para Colesterol Alto:
- Evitar: gorduras trans, carnes gordas, frituras, laticínios integrais
- Priorizar: peixes ricos em ômega-3, azeite, oleaginosas, fibras solúveis
- Dica Nutricional: Aumentar consumo de fibras (aveia, feijão), preferir carnes magras
`;
    }

    // Doença Celíaca / Intolerância ao Glúten
    if (conditionsLower.includes('celiac') || conditionsLower.includes('glúten') || conditionsLower.includes('gluten')) {
      guidance += `\nOrientações para Doença Celíaca/Intolerância ao Glúten:
- Evitar: trigo, centeio, cevada, aveia contaminada, alimentos processados com glúten
- Priorizar: arroz, quinoa, milho, mandioca, batata, tapioca, alimentos naturalmente sem glúten
- Dica Nutricional: Verificar rótulos, evitar contaminação cruzada
`;
    }

    // Obesidade
    if (conditionsLower.includes('obesidade') || conditionsLower.includes('sobrepeso')) {
      guidance += `\nOrientações para Obesidade/Sobrepeso:
- Evitar: alimentos ultraprocessados, refrigerantes, frituras, fast food
- Priorizar: alimentos integrais, proteínas magras, vegetais, frutas
- Dica Nutricional: Controlar porções, aumentar atividade física, comer devagar
`;
    }

    // Gastrite ou Refluxo
    if (conditionsLower.includes('gastrite') || conditionsLower.includes('refluxo')) {
      guidance += `\nOrientações para Gastrite/Refluxo:
- Evitar: alimentos ácidos, café, álcool, frituras, condimentos picantes
- Priorizar: alimentos leves, proteínas magras, vegetais cozidos
- Dica Nutricional: Fazer refeições menores, evitar deitar após comer
`;
    }

    return guidance;
  }

  async generateMealPlan(patientData: PatientData, nutritionCalc?: NutritionCalculation): Promise<MealPlanResponse> {
    return this.generateMealPlanViaDeepSeek(patientData, nutritionCalc);
  }

  /**
   * Gera plano alimentar via endpoint privado da DeepSeek
   */
  private async generateMealPlanViaDeepSeek(patientData: PatientData, nutritionCalc?: NutritionCalculation): Promise<MealPlanResponse> {
    const trainingSchedule = patientData.trainingTime ? getTrainingMealSchedule(patientData.trainingTime) : null;
    const mealNames: Record<string, string> = {
      breakfast: 'café da manhã', morningSnack: 'lanche da manhã', lunch: 'almoço',
      afternoonSnack: 'lanche da tarde', dinner: 'jantar', eveningSnack: 'ceia',
    };
    // Construir seção de cálculos nutricionais
    const nutritionSection = nutritionCalc ? `
Dados Nutricionais Calculados (use estes valores como base):
- TMB (Taxa Metabólica Basal): ${nutritionCalc.bmr} kcal
- TDEE (Gasto Energético Total): ${nutritionCalc.tdee} kcal
- Calorias Alvo: ${nutritionCalc.targetCalories} kcal
- Macros Alvo: Proteína ${nutritionCalc.macros.protein}g (${nutritionCalc.macrosPercent.protein}%), Carboidratos ${nutritionCalc.macros.carbohydrates}g (${nutritionCalc.macrosPercent.carbohydrates}%), Gorduras ${nutritionCalc.macros.fats}g (${nutritionCalc.macrosPercent.fats}%)
- Protocolo usado: ${nutritionCalc.protocolName || 'Mifflin-St Jeor'}
- Tipo de pessoa: ${nutritionCalc.personTypeName || 'Adulto'}
- Necessidade hídrica: ${nutritionCalc.waterIntake}ml/dia

Objetivo Nutricional:
${patientData.goal === 'weight_loss' ? 'PERDA DE PESO - Déficit calórico de aproximadamente 500kcal/dia. Priorizar proteínas e reduzir carboidratos refinados.' :
        patientData.goal === 'muscle_gain' ? 'GANHO DE MASSA MUSCULAR - Superávit calórico de aproximadamente 300kcal/dia. Priorizar proteínas e carboidratos para recuperação.' :
          'MANUTENÇÃO - Equilíbrio calórico. Manter distribuição equilibrada de macronutrientes.'}
` : '';

    const prompt = `
Crie um plano alimentar personalizado para um cliente com as seguintes características.

Dados Básicos
Nome: ${patientData.name}
Idade: ${patientData.age} anos
Peso: ${patientData.weight} kg
Altura: ${patientData.height} cm
Gênero: ${patientData.gender === 'male' ? 'Masculino' : 'Feminino'}
Nível de atividade: ${this.getActivityLevelText(patientData.activityLevel)}
Objetivo: ${this.getGoalText(patientData.goal)}
${patientData.trainingTime ? `Horário habitual do treino: ${patientData.trainingTime}. Organize os horários das refeições em torno desse horário. Use ${trainingSchedule?.preWorkoutTime} como horário de referência para o pré-treino (${mealNames[trainingSchedule?.preWorkoutMeal || ''] || 'refeição'}), e ${trainingSchedule?.postWorkoutTime} como referência para a refeição pós-treino (${mealNames[trainingSchedule?.postWorkoutMeal || ''] || 'refeição seguinte'}). Distribua as demais refeições sem sobreposição. Evite recomendar suplementação; use alimentos comuns e respeite alergias e condições clínicas.` : ''}
${nutritionSection}

⚠️ RESTRIÇÕES ALIMENTARES CRÍTICAS (OBRIGATÓRIO SEGUIR):
${patientData.allergies || (Array.isArray(patientData.dislikes) && patientData.dislikes.length > 0) ? `
${patientData.allergies ? `❌ ALERGIAS (NUNCA INCLUIR): ${patientData.allergies}` : ''}
${Array.isArray(patientData.dislikes) && patientData.dislikes.length > 0 ? `❌ AVERSÕES (NUNCA INCLUIR): ${patientData.dislikes.join(', ')}` : ''}
` : 'Nenhuma restrição alimentar informada.'}
${patientData.preferences ? `✅ PREFERÊNCIAS (PRIORIZAR): ${patientData.preferences}
` : ''}

Condições Médicas e Orientações Nutricionais:
${patientData.medicalConditions ? this.getMedicalConditionsGuidance(patientData.medicalConditions) : 'Nenhuma condição médica informada.'}
${Array.isArray(patientData.dietaryRestrictions) && patientData.dietaryRestrictions.length ? `
Restrições Dietéticas: ${patientData.dietaryRestrictions.join(', ')}` : ''}
${Array.isArray(patientData.medications) && patientData.medications.length ? `Medicamentos em uso: ${patientData.medications.join(', ')}` : ''}
${Array.isArray(patientData.supplements) && patientData.supplements.length ? `Suplementos em uso: ${patientData.supplements.join(', ')}` : ''}

Crie um plano alimentar SEMANAL COMPLETO (7 dias) com 6 refeições por dia (café da manhã, lanche da manhã, almoço, lanche da tarde, jantar, ceia).

IMPORTANTE: Varie os alimentos ao longo da semana para evitar monotonia e garantir variedade nutricional.

SUBSTITUIÇÕES OBRIGATÓRIAS - CRÍTICO:
1. Para TODAS as 6 refeições (breakfast, morningSnack, lunch, afternoonSnack, dinner, eveningSnack) de TODOS os 7 dias, você DEVE fornecer 2-3 opções alternativas nutricionalmente equivalentes (±10% de tolerância em calorias e macros).
2. NÃO inclua substituições apenas para o café da manhã. TODAS as refeições precisam ter opções alternativas.
3. Para alimentos principais (proteínas como frango, peixe, carne; carboidratos como arroz, batata, macarrão), forneça alternativas de substituição individual.
4. Para manter a resposta completa, use exatamente 2 opções por refeição, descrições curtas e quantidades objetivas; não inclua explicações ou texto fora do JSON.

Responda APENAS com um JSON válido no seguinte formato:

{
  "dailyCalories": 2000,
  "macronutrients": {
    "protein": 150,
    "carbohydrates": 200,
    "fats": 67
  },
  "weeklyPlan": {
    "monday": {
      "breakfast": ["1 xícara de aveia com banana", "1 copo de leite desnatado"],
      "morningSnack": ["1 maçã", "10 castanhas"],
      "lunch": ["3 colheres de arroz integral", "1 concha de feijão", "100g de frango grelhado", "Salada verde"],
      "afternoonSnack": ["1 iogurte natural", "1 colher de granola"],
      "dinner": ["2 colheres de quinoa", "100g de salmão", "Legumes refogados"],
      "eveningSnack": ["1 chá verde", "1 fatia de pão integral com queijo branco"]
    },
    "tuesday": { ... },
    "wednesday": { ... },
    "thursday": { ... },
    "friday": { ... },
    "saturday": { ... },
    "sunday": { ... }
  },
  "substitutions": {
    "mealSubstitutions": [
      {
        "mealType": "breakfast",
        "day": "monday",
        "options": [
          {
            "id": "opt1",
            "foods": ["1 xícara de aveia com banana", "1 copo de leite desnatado"],
            "calories": 350,
            "macros": { "protein": 15, "carbohydrates": 55, "fats": 8 }
          },
          {
            "id": "opt2",
            "foods": ["2 ovos mexidos", "2 fatias de pão integral", "1 copo de suco de laranja"],
            "calories": 380,
            "macros": { "protein": 18, "carbohydrates": 50, "fats": 12 }
          },
          {
            "id": "opt3",
            "foods": ["1 iogurte grego", "1 colher de granola", "1 banana"],
            "calories": 360,
            "macros": { "protein": 16, "carbohydrates": 52, "fats": 10 }
          }
        ]
      },
      {
        "mealType": "lunch",
        "day": "monday",
        "options": [
          {
            "id": "opt1",
            "foods": ["3 colheres de arroz integral", "1 concha de feijão", "100g de frango grelhado", "Salada verde"],
            "calories": 520,
            "macros": { "protein": 35, "carbohydrates": 65, "fats": 12 }
          },
          {
            "id": "opt2",
            "foods": ["4 colheres de macarrão integral", "100g de carne moída magra", "Brócolis refogado"],
            "calories": 540,
            "macros": { "protein": 38, "carbohydrates": 68, "fats": 14 }
          },
          {
            "id": "opt3",
            "foods": ["2 colheres de quinoa", "120g de peixe assado", "Legumes no vapor", "Salada"],
            "calories": 510,
            "macros": { "protein": 36, "carbohydrates": 62, "fats": 13 }
          }
        ]
      },
      {
        "mealType": "dinner",
        "day": "monday",
        "options": [
          {
            "id": "opt1",
            "foods": ["2 colheres de quinoa", "100g de salmão", "Legumes refogados"],
            "calories": 450,
            "macros": { "protein": 32, "carbohydrates": 45, "fats": 15 }
          },
          {
            "id": "opt2",
            "foods": ["150g de peixe branco assado", "Batata doce assada", "Salada de folhas"],
            "calories": 470,
            "macros": { "protein": 34, "carbohydrates": 48, "fats": 14 }
          },
          {
            "id": "opt3",
            "foods": ["100g de frango grelhado", "Abóbora assada", "Couve refogada"],
            "calories": 440,
            "macros": { "protein": 33, "carbohydrates": 42, "fats": 13 }
          }
        ]
      },
      {
        "mealType": "morningSnack",
        "day": "monday",
        "options": [
          {
            "id": "opt1",
            "foods": ["1 maçã", "10 castanhas"],
            "calories": 180,
            "macros": { "protein": 4, "carbohydrates": 25, "fats": 8 }
          },
          {
            "id": "opt2",
            "foods": ["1 pera", "1 punhado de amêndoas"],
            "calories": 190,
            "macros": { "protein": 5, "carbohydrates": 26, "fats": 9 }
          }
        ]
      }
    ],
    "foodSubstitutions": [
      {
        "originalFood": "100g de frango grelhado",
        "mealType": "lunch",
        "day": "monday",
        "alternatives": [
          { "food": "100g de peito de peru", "quantity": "100g", "equivalentMacros": true },
          { "food": "120g de peixe branco", "quantity": "120g", "equivalentMacros": true }
        ]
      },
      {
        "originalFood": "3 colheres de arroz integral",
        "mealType": "lunch",
        "day": "monday",
        "alternatives": [
          { "food": "2 colheres de quinoa", "quantity": "2 colheres", "equivalentMacros": true },
          { "food": "4 colheres de macarrão integral", "quantity": "4 colheres", "equivalentMacros": true }
        ]
      }
    ]
  },
  "hydration": "2L de água por dia",
  "notes": "Consumir refeições a cada 3-4 horas. Evitar frituras e doces."
}

IMPORTANTE: 
- Responda APENAS o JSON, sem texto adicional.
- O weeklyPlan deve conter TODOS os 7 dias (monday, tuesday, wednesday, thursday, friday, saturday, sunday).
- Cada dia deve ter as 6 refeições (breakfast, morningSnack, lunch, afternoonSnack, dinner, eveningSnack).
- Cada refeição deve ser um array de strings com os alimentos e quantidades.
- Varie os alimentos entre os dias para evitar repetição.
- OBRIGATÓRIO: Inclua substituições (mealSubstitutions) para TODAS as 6 refeições de TODOS os 7 dias. Cada refeição deve ter pelo menos 2-3 opções alternativas.
- As substituições devem manter valores nutricionais similares (±10% de tolerância).
- NÃO se limite apenas ao café da manhã - inclua substituições para almoço, jantar, lanches, etc.
`;

    try {
      const text = await this.requestDeepSeek(prompt);

      // Limpar texto antes e depois do JSON
      let cleanText = text.trim();
      const jsonStart = cleanText.indexOf('{');
      const jsonEnd = cleanText.lastIndexOf('}');

      if (jsonStart !== -1 && jsonEnd !== -1) {
        cleanText = cleanText.substring(jsonStart, jsonEnd + 1);
      }

      let parsed: MealPlanResponse;
      try {
        parsed = JSON.parse(cleanText) as MealPlanResponse;
      } catch (parseError) {
        console.error('A resposta estruturada da DeepSeek não pôde ser interpretada.', parseError);
        throw new Error('A resposta da DeepSeek veio incompleta ou fora do formato esperado. Tente gerar o plano novamente.');
      }

      // Validar estrutura
      if (!parsed.dailyCalories) {
        throw new Error('Resposta da IA não contém estrutura válida');
      }

      // Se não tiver weeklyPlan, criar um plano semanal a partir do meals (replicar para todos os dias)
      if (!parsed.weeklyPlan && parsed.meals) {
        const dayMeals: DayMeals = {
          breakfast: parsed.meals.breakfast || [],
          morningSnack: parsed.meals.morningSnack || [],
          lunch: parsed.meals.lunch || [],
          afternoonSnack: parsed.meals.afternoonSnack || [],
          dinner: parsed.meals.dinner || [],
          eveningSnack: parsed.meals.eveningSnack || [],
        };
        parsed.weeklyPlan = {
          monday: { ...dayMeals },
          tuesday: { ...dayMeals },
          wednesday: { ...dayMeals },
          thursday: { ...dayMeals },
          friday: { ...dayMeals },
          saturday: { ...dayMeals },
          sunday: { ...dayMeals },
        };
      }

      // Validar e processar substituições se existirem
      if (parsed.substitutions) {
        parsed.substitutions = this.validateSubstitutions(parsed.substitutions);
      }

      // Adicionar valores de cálculo nutricional se fornecidos
      if (nutritionCalc) {
        parsed.bmr = nutritionCalc.bmr;
        parsed.tdee = nutritionCalc.tdee;
        parsed.targetCalories = nutritionCalc.targetCalories;
      }

      return parsed;
    } catch (error: any) {
      const errorMsg = error?.message || 'Erro desconhecido ao gerar plano';
      throw new Error(`Erro ao gerar plano alimentar via DeepSeek: ${errorMsg}`);
    }
  }

  /**
   * Valida substituições retornadas pela IA
   * Verifica equivalência nutricional (±10% tolerância)
   */
  private validateSubstitutions(substitutions: MealPlanSubstitutions): MealPlanSubstitutions {
    // Validar substituições de refeições
    const validatedMealSubs = substitutions.mealSubstitutions?.map(sub => {
      if (!sub.options || sub.options.length < 2) {
        return sub; // Manter mesmo se não tiver opções suficientes
      }

      // Validar que as opções são nutricionalmente equivalentes
      const baseOption = sub.options[0];
      const validatedOptions = sub.options.map(opt => {
        // Verificar se está dentro da tolerância de ±10%
        const calorieDiff = Math.abs(opt.calories - baseOption.calories) / baseOption.calories;
        const proteinDiff = Math.abs(opt.macros.protein - baseOption.macros.protein) / baseOption.macros.protein;
        const carbDiff = Math.abs(opt.macros.carbohydrates - baseOption.macros.carbohydrates) / baseOption.macros.carbohydrates;
        const fatDiff = Math.abs(opt.macros.fats - baseOption.macros.fats) / baseOption.macros.fats;

        // Se estiver fora da tolerância, marcar mas manter
        if (calorieDiff > 0.1 || proteinDiff > 0.1 || carbDiff > 0.1 || fatDiff > 0.1) {
          console.warn(`Substituição pode estar fora da tolerância de ±10%: ${opt.id}`);
        }

        return opt;
      });

      return {
        ...sub,
        options: validatedOptions
      };
    }) || [];

    // Validar substituições de alimentos
    const validatedFoodSubs = substitutions.foodSubstitutions?.map(sub => {
      return {
        ...sub,
        alternatives: sub.alternatives || []
      };
    }) || [];

    return {
      mealSubstitutions: validatedMealSubs,
      foodSubstitutions: validatedFoodSubs
    };
  }
}
